import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { api, session } from '../../shell/bridge';
import { Pressable } from '../../ui/Pressable';
import { SkeletonBlock } from '../../ui/Skeleton';
import './requests.css';

interface RecurrenceRule {
  freq: 'daily' | 'weekly' | 'monthly';
  nextDueAt: number;
}

interface RequestItem {
  requestId: string;
  fromMemberId: string;
  toMemberId: string | null;
  text: string;
  state: 'posted' | 'countered' | 'accepted' | 'declined';
  counterText: string | null;
  recurrence: RecurrenceRule | null;
  createdAt: number;
  updatedAt: number;
  spawnedFromId: string | null;
}

const STATE_TONE: Record<RequestItem['state'], string> = {
  posted: 'is-pending',
  countered: 'is-countered',
  accepted: 'is-accepted',
  declined: 'is-declined',
};
const STATE_LABEL: Record<RequestItem['state'], string> = {
  posted: 'Pending',
  countered: 'Countered',
  accepted: 'Accepted',
  declined: 'Declined',
};

const REPEAT_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'No repeat' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

/**
 * Requests panel — composer is a single card (full-width input, repeat as
 * chip options, Post right-aligned), posted asks carry a status chip, and the
 * empty state invites a real ask instead of apologising.
 */
export function RequestsPanel() {
  const me = session();
  const [items, setItems] = useState<RequestItem[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [draft, setDraft] = useState('');
  const [freq, setFreq] = useState('');
  const [posting, setPosting] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [counterDrafts, setCounterDrafts] = useState<Record<string, string>>({});

  const reload = useCallback(async () => {
    try {
      const data = await api<{ requests: RequestItem[] }>('/api/requests');
      setItems(data.requests ?? []);
      setFailed(false);
    } catch (e) {
      console.error('[Pear] requests load failed', e);
      setItems([]);
      setFailed(true);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const post = async (e: FormEvent) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text || posting) return;
    setPosting(true);
    try {
      const body: Record<string, unknown> = { text };
      if (freq) body.recurrence = { freq, nextDueAt: Date.now() + 60_000 };
      await api('/api/requests', { body });
      setDraft('');
      setFreq('');
      await reload();
    } catch (err) {
      console.error('[Pear] post failed', err);
    } finally {
      setPosting(false);
    }
  };

  const transition = async (item: RequestItem, action: 'accept' | 'counter' | 'decline') => {
    if (busyId) return;
    const body: Record<string, unknown> = { action };
    if (action === 'counter') {
      const text = (counterDrafts[item.requestId] ?? '').trim();
      if (!text) return;
      body.counterText = text;
    }
    setBusyId(item.requestId);
    try {
      await api(`/api/requests/${item.requestId}/transition`, { body });
      setCounterDrafts(prev => {
        const next = { ...prev };
        delete next[item.requestId];
        return next;
      });
      await reload();
    } catch (e) {
      console.error('[Pear] transition failed', e);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="rq-panel">
      <h2 className="rq-title">Requests</h2>

      <form className="rq-card rq-composer" onSubmit={post}>
        <label className="rq-composer-label" htmlFor="rq-input">
          Ask for something
        </label>
        <input
          id="rq-input"
          type="text"
          className="pear-chat-input rq-input"
          placeholder="e.g. a lie-in on Saturday"
          aria-label="New request"
          value={draft}
          onChange={e => setDraft(e.target.value)}
        />
        <div className="rq-composer-foot">
          <div className="rq-repeats" role="group" aria-label="Repeat">
            {REPEAT_OPTIONS.map(opt => (
              <button
                key={opt.value}
                type="button"
                aria-pressed={freq === opt.value}
                className={`rq-repeat-chip${freq === opt.value ? ' is-on' : ''}`}
                onClick={() => setFreq(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <button type="submit" className="pear-btn pear-btn-mustard rq-post" disabled={posting || draft.trim().length === 0}>
            {posting ? 'Posting…' : 'Post'}
          </button>
        </div>
      </form>

      <div className="rq-list">
        {items === null && !failed && <SkeletonBlock lines={3} />}

        {failed && <p className="rq-empty">Could not load requests.</p>}

        {items !== null && items.length === 0 && !failed && (
          <div className="rq-empty-state">
            <p className="rq-empty-title">No requests yet.</p>
            <p className="rq-empty-hint">Ask for something small — a lie-in, a shared dinner, an evening walk.</p>
          </div>
        )}

        {items?.map(item => {
          const isAuthor = !!me && item.fromMemberId === me.memberId;
          const actionable = (item.state === 'posted' && !isAuthor) || (item.state === 'countered' && isAuthor);
          const busy = busyId === item.requestId;
          return (
            <div className="pear-request-row rq-row" key={item.requestId}>
              <div className="rq-head">
                <span className={`rq-state ${STATE_TONE[item.state]}`}>{STATE_LABEL[item.state]}</span>
                <span className="rq-when">{new Date(item.createdAt).toLocaleDateString()}</span>
              </div>

              <p className="pear-request-text rq-text">{item.text}</p>

              {item.counterText && <p className="pear-request-counter rq-counter">Counter: {item.counterText}</p>}
              {item.recurrence && <span className="rq-recurrence">↺ {item.recurrence.freq}</span>}

              {actionable && (
                <div className="rq-actions">
                  <Pressable
                    className="pear-btn pear-btn-mustard rq-action"
                    disabled={busy}
                    onClick={() => void transition(item, 'accept')}
                  >
                    Accept
                  </Pressable>

                  <input
                    type="text"
                    className="pear-chat-input rq-counter-input"
                    placeholder="Suggest an alternative…"
                    aria-label="Counter suggestion"
                    value={counterDrafts[item.requestId] ?? ''}
                    onChange={e => setCounterDrafts(prev => ({ ...prev, [item.requestId]: e.target.value }))}
                  />
                  <Pressable
                    className="pear-btn rq-action rq-action-plain"
                    disabled={busy}
                    onClick={() => void transition(item, 'counter')}
                  >
                    Counter
                  </Pressable>

                  <Pressable
                    className="pear-btn pear-btn-blush rq-action"
                    disabled={busy}
                    onClick={() => void transition(item, 'decline')}
                  >
                    Decline
                  </Pressable>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}